import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';

/**
 * Fetches Call List data entirely from the database via the
 * get_call_list_leads RPC.  No client-side eligibility filtering.
 */
export function useCallListDB({
  searchTerm       = '',
  sortBy           = 'score',
  sortOrder        = 'desc',
  filterStepOpens  = '',
  filterOpenSteps  = '',
  filterTotalOpens = '',
  filterCampaigns  = [],
  page             = 1,
  pageSize         = 10,
}) {
  const { currentUser } = useAuth();
  const { assignedCampaigns, isAdminOrHigher } = useRBAC();

  const [leads, setLeads]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  // Track latest request to avoid race conditions
  const reqId = useRef(0);

  const fetch = useCallback(async () => {
    if (!currentUser?.email) return;

    // RBAC campaigns: null = admin (all), [] = no access, [...] = restricted
    const campaigns = isAdminOrHigher()
      ? null
      : (assignedCampaigns?.length ? assignedCampaigns : []);

    const thisReq = ++reqId.current;
    setLoading(true);
    setError(null);

    try {
      const { data: result, error: rpcErr } = await supabase.rpc('get_call_list_leads', {
        p_user_email:         currentUser.email.toLowerCase(),
        p_campaigns:          campaigns,
        p_search:             searchTerm  ? searchTerm.trim()  : null,
        p_sort_by:            sortBy      || 'score',
        p_sort_order:         sortOrder   || 'desc',
        p_filter_step:        filterStepOpens  ? parseInt(filterStepOpens,  10) : null,
        p_filter_open_steps:  filterOpenSteps  ? parseInt(filterOpenSteps,  10) : null,
        p_filter_total_opens: filterTotalOpens ? parseInt(filterTotalOpens, 10) : null,
        p_filter_campaigns:   filterCampaigns?.length ? filterCampaigns : null,
        p_page:               page     || 1,
        p_page_size:          pageSize || 10,
      });

      if (thisReq !== reqId.current) return; // stale response
      if (rpcErr) throw rpcErr;

      setLeads(result?.leads      ?? []);
      setTotal(result?.total      ?? 0);
      setTotalPages(result?.total_pages ?? 0);
    } catch (err) {
      if (thisReq === reqId.current) setError(err.message);
    } finally {
      if (thisReq === reqId.current) setLoading(false);
    }
  }, [
    currentUser?.email,
    assignedCampaigns,
    isAdminOrHigher,
    searchTerm, sortBy, sortOrder,
    filterStepOpens, filterOpenSteps, filterTotalOpens,
    filterCampaigns,
    page, pageSize,
  ]);

  useEffect(() => { fetch(); }, [fetch]);

  // Real-time: re-fetch whenever leads, lead_events, or executions change
  useEffect(() => {
    const channel = supabase
      .channel('calllist-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' },           () => fetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lead_events' },     () => fetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_automation_executions' }, () => fetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_call_list_leads' }, () => fetch())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  return { leads, total, totalPages, loading, error, refetch: fetch };
}
